import { useEffect, useState } from 'react';
import { usePageTitle } from '@/hooks/use-page-title';
import { useApplicationDetail } from '@/hooks/use-entity-detail';
import { useParams, Link, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useHeaderAction } from '@/lib/header-action-context';
import { api } from '@/lib/api-client';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { getCapabilityColors } from '@/lib/capability-colors';
import { AppWindow, ArrowRight, Archive, Database, FileArchive } from 'lucide-react';
import { toast } from 'sonner';
import ExportApplicationDrawer from '@/components/applications/ExportApplicationDrawer';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';

/** Same order and set as reference admin app dashboard (e.g. /admin/apps/[id]) */
const CAPABILITY_CARDS: { type: string; linkType: string }[] = [
    { type: 'workflow', linkType: 'workflow' },
    { type: 'agent', linkType: 'agent' },
    { type: 'model', linkType: 'model' },
    { type: 'prompt', linkType: 'prompt' },
    { type: 'tool', linkType: 'tool' },
    { type: 'skill', linkType: 'skill' },
    { type: 'role', linkType: 'role' },
    { type: 'policy', linkType: 'policy' },
    { type: 'permission', linkType: 'permission' },
    { type: 'decision', linkType: 'decision' },
    { type: 'schema', linkType: 'schema' },
    { type: 'subscription', linkType: 'subscription' },
    { type: 'domain_type', linkType: 'domain-type' },
    { type: 'binding', linkType: 'binding' },
    { type: 'sandbox', linkType: 'sandbox' },
    { type: 'workspace', linkType: 'workspace' },
    { type: 'provider', linkType: 'provider' },
    { type: 'worker', linkType: 'worker' },
    { type: 'integration', linkType: 'integration' },
    { type: 'connection', linkType: 'connection' },
];

const statusBadgeStyles: Record<string, string> = {
    active: 'bg-emerald-100 text-emerald-700',
    suspended: 'bg-amber-100 text-amber-700',
    archived: 'bg-red-100 text-red-700',
    deleted: 'bg-red-100 text-red-700',
};

function getMetricCount(metrics: Record<string, number>, type: string): number {
    const variants = [type, type.replace(/-/g, '_'), type.replace(/_/g, '-')];
    for (const v of variants) {
        const n = metrics[v];
        if (n != null) return n;
        const nl = metrics[v.toLowerCase()];
        if (nl != null) return nl;
    }
    return 0;
}

export default function ApplicationDetailPage() {
    const { appKey } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { data, isLoading } = useApplicationDetail(appKey!);
    const { setHeaderAction, setHeaderTitle } = useHeaderAction();
    const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
    const [isArchiving, setIsArchiving] = useState(false);
    const [exportDrawerOpen, setExportDrawerOpen] = useState(false);
    usePageTitle(`Application - ${appKey}`);

    const app = data?.application as {
        name?: string;
        app_key?: string;
        description?: string;
        status?: string;
        team_name?: string;
        schema_name?: string;
        created?: string;
        config?: Record<string, unknown>;
    } | undefined;
    const appName = app?.name ?? appKey ?? 'Application';

    const isArchived = (app?.status ?? '').toLowerCase() === 'archived';

    useEffect(() => {
        if (isLoading || !app || !appKey) {
            setHeaderTitle(null);
            return;
        }
        const appColors = getCapabilityColors('application');
        const AppIcon = appColors.icon;
        const badgeStyle = statusBadgeStyles[app.status ?? ''] ?? 'bg-slate-100 text-slate-700';
        setHeaderTitle({
            icon: (
                <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md ${appColors.bgLight}`}>
                    <AppIcon size={16} className={appColors.text} />
                </div>
            ),
            title: app.name ?? appKey,
            subtitle: app.app_key ?? appKey,
            badges: (
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium uppercase ${badgeStyle}`}>
                    {app.status ?? 'active'}
                </span>
            ),
        });
        return () => setHeaderTitle(null);
    }, [isLoading, app, appKey, setHeaderTitle]);

    useEffect(() => {
        if (!isLoading && app) {
            setHeaderAction(
                <div className="flex items-center gap-2">
                    <Button
                        type="button"
                        size="sm"
                        onClick={() => setExportDrawerOpen(true)}
                        className="inline-flex items-center justify-center gap-2 rounded-md bg-btn-primary px-3 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-btn-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
                    >
                        <FileArchive size={14} />
                        Export Application
                    </Button>
                    {!isArchived && (
                        <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            onClick={() => setArchiveDialogOpen(true)}
                            className="inline-flex items-center gap-1.5"
                        >
                            <Archive size={14} />
                            Archive Application
                        </Button>
                    )}
                </div>
            );
        } else {
            setHeaderAction(null);
        }
        return () => setHeaderAction(null);
    }, [isLoading, app, isArchived, setHeaderAction]);

    async function handleArchive() {
        if (!appKey) return;
        setIsArchiving(true);
        try {
            await api.post(`/applications/${appKey}/archive`);
            toast.success('Application archived successfully');
            queryClient.invalidateQueries({ queryKey: ['application', appKey] });
            queryClient.invalidateQueries({ queryKey: ['applications'] });
            navigate('/applications', { replace: true });
        } catch {
            toast.error('Failed to archive application');
        } finally {
            setIsArchiving(false);
            setArchiveDialogOpen(false);
        }
    }

    if (isLoading) return <div className="p-6">Loading...</div>;

    const metrics: Record<string, number> = data?.metrics || {};
    const applicationCapabilityDescription = getCapabilityColors('application').description;
    const visibleCapabilityCards = CAPABILITY_CARDS.filter(
        ({ type }) => getMetricCount(metrics, type) > 0,
    );
    const totalCapabilities = visibleCapabilityCards.reduce(
        (sum, { type }) => sum + getMetricCount(metrics, type),
        0,
    );
    return (
        <div className="space-y-8 p-6">
            {/* Capability-style application summary */}
            <Card
                data-slot="page-body-header"
                className="mx-auto w-full max-w-5xl gap-0 border border-[hsl(var(--border))] border-l-4 border-l-cyan-400 bg-[hsl(var(--page-header-background))] py-5 text-[hsl(var(--page-header-foreground))] shadow-sm"
            >
                <div className="relative flex min-h-[120px] flex-col px-5">
                    <div className="flex items-start gap-3">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-cyan-100 text-cyan-700">
                            <AppWindow size={20} />
                        </div>
                        <div className="min-w-0">
                            <h1 className="text-lg font-bold leading-tight text-[hsl(var(--page-header-foreground))]">
                                {appName}
                            </h1>
                            <p className="mt-0.5 font-mono text-xs text-[hsl(var(--page-header-muted-foreground))]">
                                {app?.app_key ?? appKey}
                            </p>
                            <p className="mt-1 text-xs text-[hsl(var(--page-header-muted-foreground))]">
                                {applicationCapabilityDescription}
                            </p>
                            {app?.description && (
                                <p className="mt-2 line-clamp-2 text-sm leading-tight text-[hsl(var(--page-header-foreground))]">
                                    {String(app.description)}
                                </p>
                            )}
                        </div>
                    </div>

                    <div className="mt-auto flex items-center gap-3 border-t border-[hsl(var(--border))] pt-2 text-xs text-[hsl(var(--page-header-muted-foreground))]">
                        <span className="flex items-center gap-1">
                            <Database size={12} />
                            {totalCapabilities} capabilities
                        </span>
                        {app?.team_name && <span>Team: {String(app.team_name)}</span>}
                        {app?.schema_name && (
                            <span className="font-mono">Schema: {String(app.schema_name)}</span>
                        )}
                        {app?.created && (
                            <span className="ml-auto text-[hsl(var(--page-header-muted-foreground))]">
                                Created {new Date(String(app.created)).toLocaleDateString()}
                            </span>
                        )}
                    </div>
                </div>
            </Card>

            {/* Capability cards only for types with a positive metric count */}
            {visibleCapabilityCards.length > 0 && (
            <div className="px-30">
                <h2 className="mb-4 font-semibold text-slate-900">Capabilities</h2>
                <div className="flex flex-wrap justify-center gap-4">
                    {visibleCapabilityCards.map(({ type, linkType }) => {
                        const count = getMetricCount(metrics, type);
                        const colors = getCapabilityColors(type);
                        const Icon = colors.icon;
                        return (
                            <Link
                                key={type}
                                to={`/applications/${appKey}/capabilities?type=${linkType}`}
                                className="block w-[270px]"
                            >
                                <Card
                                    className={`group flex h-[89px] min-h-[89px] flex-col overflow-hidden border-l-4 p-3 ${colors.border} ${colors.hover} cursor-pointer transition-colors`}
                                >
                                    <div className="flex h-full min-h-0 items-start justify-between gap-2">
                                        <div className="flex min-h-0 min-w-0 flex-1 items-start gap-3">
                                            <div
                                                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${colors.bgLight}`}
                                            >
                                                <Icon size={18} className={colors.text} />
                                            </div>
                                            <div className="min-h-0 min-w-0 flex-1">
                                                <p className="line-clamp-1 text-base font-semibold text-slate-900">
                                                    {colors.label}
                                                </p>
                                                <p className="mt-0.5 line-clamp-1 text-sm leading-tight text-slate-500">
                                                    {colors.description}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex shrink-0 items-center gap-2">
                                            <span className="text-2xl font-bold tabular-nums text-slate-900">
                                                {count}
                                            </span>
                                            <ArrowRight
                                                size={16}
                                                className="shrink-0 text-slate-400 transition-transform group-hover:translate-x-1"
                                            />
                                        </div>
                                    </div>
                                </Card>
                            </Link>
                        );
                    })}
                </div>
            </div>
            )}

            {/* Configuration */}
            {app?.config && typeof app.config === 'object' && Object.keys(app.config as object).length > 0 && (
                <div>
                    <h2 className="font-semibold text-slate-900 mb-4">Configuration</h2>
                    <pre className="rounded-lg bg-slate-50 border p-4 text-sm overflow-auto text-slate-700">
                        {JSON.stringify(app.config, null, 2)}
                    </pre>
                </div>
            )}

            <ExportApplicationDrawer
                isOpen={exportDrawerOpen}
                onClose={() => setExportDrawerOpen(false)}
                appKey={appKey!}
            />

            <AlertDialog open={archiveDialogOpen} onOpenChange={setArchiveDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                            <Archive size={18} className="text-destructive" />
                            Archive Application
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to archive &quot;{appName}&quot;? It will no longer appear in the active applications list.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={isArchiving}>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(e) => {
                                e.preventDefault();
                                handleArchive();
                            }}
                            disabled={isArchiving}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            {isArchiving ? 'Archiving...' : 'Archive'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
